/**
 * LearnPiecePicker — "learn this piece" (docs/design/STEAL-SPEC.md "UI").
 *
 * Opened from MetadataBanner's tertiary "learn this piece" button, shown
 * only when the canvas holds an analyzed piece. Pick an existing influence
 * to accumulate onto, or name a new one — extraction + accumulation run in
 * influences-store's learnPiece, which also fires the handwriting toast
 * ("learned — nine pieces now inside Lauridsen").
 */
import React, { useEffect, useState, useCallback } from 'react';
import type { Chord, Mode } from '@/types/chord';
import { useModalStore } from '@/store/modal-store';
import { useInfluencesStore } from '@/store/influences-store';
import styles from './LearnPiecePicker.module.css';

interface LearnPiecePickerProps {
  chords: Chord[];
  mode: Mode;
  pieceName: string;
  /** Composer from the analyzed piece's metadata, when known — pre-fills
   *  the "name a new influence" field (still editable) so the common case
   *  of learning a piece into an influence named after its composer
   *  doesn't require retyping the name. */
  composerName?: string;
}

export const LearnPiecePicker: React.FC<LearnPiecePickerProps> = ({ chords, mode, pieceName, composerName }) => {
  const isOpen = useModalStore((s) => s.isOpen('learnPiece'));
  const closePicker = useCallback(() => useModalStore.getState().closeModal('learnPiece'), []);
  const influences = useInfluencesStore((s) => s.influences);
  const loadInfluences = useInfluencesStore((s) => s.loadInfluences);
  const learnPiece = useInfluencesStore((s) => s.learnPiece);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadInfluences();
      // Pre-fill from the analysed piece's composer, if known; still editable.
      setNewName(composerName?.trim() || '');
    }
  }, [isOpen, loadInfluences, composerName]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closePicker]);

  if (!isOpen) return null;

  const handlePick = async (influenceId?: string) => {
    await learnPiece({
      influenceId,
      newInfluenceName: influenceId ? undefined : newName.trim() || undefined,
      chords,
      mode,
      pieceName,
    });
    setNewName('');
    closePicker();
  };

  return (
    <div className={styles.dock}>
      <div className={styles.picker} role="dialog" aria-label="Learn this piece">
        <div className={styles.header}>learn this piece as…</div>

        {influences.length > 0 && (
          <ul className={styles.list}>
            {influences.map((influence) => (
              <li key={influence.id}>
                <button type="button" className={styles.option} onClick={() => handlePick(influence.id)}>
                  {influence.name}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.newRow}>
          <input
            type="text"
            className={styles.newInput}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="or name a new influence"
            aria-label="New influence name"
          />
          <button
            type="button"
            className={styles.confirm}
            onClick={() => handlePick(undefined)}
            disabled={newName.trim().length === 0}
          >
            learn
          </button>
        </div>

        <button type="button" className={styles.cancel} onClick={closePicker}>
          cancel
        </button>
      </div>
    </div>
  );
};

LearnPiecePicker.displayName = 'LearnPiecePicker';
